import { z } from "zod";
import { Citation } from "./citation";

/**
 * Attribute Test Column Configuration
 * Defines what attribute to test and how to evaluate it
 */
export const AttributeTestColumnSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  passCriteria: z.string(),
  failCriteria: z.string().nullable(),
  naCriteria: z.string().nullable(),
  attributeId: z.string().nullable(),
  procedureGuidance: z.string().nullable(),
  tolerances: z.string().nullable(),
  expectedEvidences: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AttributeTestColumn = z.infer<typeof AttributeTestColumnSchema>;

/**
 * Citation Group
 * Groups related citations within a reperformance step
 */
export const CitationGroupSchema = z.object({
  id: z.string(),
  groupName: z.string(),
  shortSummary: z.string().nullable().optional(),
  groupOrder: z.number(),
  citations: z.array(Citation),
});

export type CitationGroup = z.infer<typeof CitationGroupSchema>;

/**
 * Reperformance Step
 * Documents individual testing steps with citations
 */
export const ReperformanceStepSchema = z.object({
  id: z.string().optional(),
  stepName: z.string(),
  stepDescription: z.string(),
  citationGroups: z.array(CitationGroupSchema).optional().default([]),
  citations: z.array(Citation),
});

export type ReperformanceStep = z.infer<typeof ReperformanceStepSchema>;

/**
 * Auditor Trail (Evidence Walk V3)
 * The decisive narrative for an attribute test: an L1 verdict line plus ordered L2 steps.
 * Each step carries a `stepType` (how to render its evidence), an `isException` flag (whether it is
 * where a control exception was noted — drives the red flag), and the citations it relies on.
 */
export const AuditorStepType = z.enum(["observation", "comparison"]);
export type AuditorStepType = z.infer<typeof AuditorStepType>;

/**
 * Where a composed row sits in the run's story: the inputs it was handed, or
 * the outputs it produced.
 *
 * There is deliberately no member for the run's own operations. An operation
 * reports itself and needs no heading composed over it, so the vocabulary
 * covers only the two rows Midship authors around them.
 */
export const AuditorTrailPhase = z.enum(["inputs", "outputs"]);
export type AuditorTrailPhase = z.infer<typeof AuditorTrailPhase>;

export const AuditorStepSchema = z.object({
  id: z.string(),
  text: z.string(),
  stepType: AuditorStepType,
  isException: z.boolean().nullable().optional().default(false),
  /**
   * Set only on a row the analytics trail composer authored, which is what
   * makes a non-null phase the mark of a composed bookend. Every other step
   * reads null — an analytics operation row, and every attribute-test step,
   * since nothing on that path composes.
   *
   * Optional as well as nullable: a backend that predates the field sends no
   * key at all.
   *
   * **The tolerance sits on the field so that it never has to sit on the
   * trail.** This schema is shared with attribute-test results, which are
   * parsed with no element tolerance, and the analytics verdict takes its trail
   * through a `.catch(null)` that discards the whole of it when any one element
   * fails — the bookends, every card citation, every star, and the review pane's
   * key-steps section with them. A third member of this enum shipping ahead of
   * this build would cost all of that. Caught here, the unfamiliar value reads
   * as null and costs that one row its place among the bookends; every other row
   * and every citation on the trail survives. The attribute-test path keeps its
   * trail whatever the backend writes on a shared row, rather than resting on
   * the backend writing nothing unfamiliar there.
   */
  phase: AuditorTrailPhase.nullable().optional().catch(null),
  citations: z.array(Citation),
});

export type AuditorStep = z.infer<typeof AuditorStepSchema>;

export const AuditorTrailSchema = z.object({
  id: z.string(),
  l1: z.string(),
  steps: z.array(AuditorStepSchema),
});

export type AuditorTrail = z.infer<typeof AuditorTrailSchema>;

/**
 * Control Exception Types
 * Maps to backend ControlExceptionsT enum
 */
export const ControlExceptionType = z.enum([
  "insufficient_evidence",
  "inconclusive_evidence",
  "untimely_execution",
  "improper_authorization",
  "inconsistent_supporting_data",
  "control_not_performed",
  "misstatement_of_amount",
  "cutoff_misclassification",
  "general_exception",
]);

export type ControlExceptionType = z.infer<typeof ControlExceptionType>;

/**
 * Performance Issue Types
 * Indicates why testing couldn't complete
 */
export const PerformanceIssueType = z.enum([
  "exhausted_search",
  // TODO: Add more types as backend defines them (timeout, conflicting_instructions, etc.)
]);

export type PerformanceIssueType = z.infer<typeof PerformanceIssueType>;

/**
 * Outcome Type
 */
export const OutcomeType = z.enum(["pass", "fail", "not_applicable"]);

export type OutcomeType = z.infer<typeof OutcomeType>;

/**
 * Conclusive Attribute Test Result
 * When is_conclusive=true
 */
export const ConclusiveAttributeTestResultSchema = z.object({
  id: z.string().optional(),
  attributeTestColumnId: z.string(),
  isConclusive: z.literal(true),
  outcome: OutcomeType,
  outcomeDescription: z.string().nullable(),
  evidenceSummary: z.string().nullable().optional(),
  potentialMitigatingFactors: z.string().nullable(),
  controlExceptions: z.array(ControlExceptionType),
  reperformanceSteps: z.array(ReperformanceStepSchema),
  inlineCitations: z.array(Citation),
  auditorTrail: AuditorTrailSchema.nullable().optional(),
  reviewOrder: z.number().default(0),
  reviewerType: z.enum(["human", "system"]).default("system"),
  userId: z.string().nullable().optional(),
  columnVersion: z.number().default(0),
  runId: z.string().nullable().optional(),
  createdAt: z.string().or(z.date()).optional(),
  updatedAt: z.string().or(z.date()).optional(),
});

export type ConclusiveAttributeTestResult = z.infer<
  typeof ConclusiveAttributeTestResultSchema
>;

/**
 * Inconclusive Attribute Test Result
 * When is_conclusive=false
 */
export const InconclusiveAttributeTestResultSchema = z.object({
  id: z.string().optional(),
  attributeTestColumnId: z.string(),
  isConclusive: z.literal(false),
  performanceIssues: z.array(PerformanceIssueType),
  partialDescription: z.string(),
  evidenceSummary: z.string().nullable().optional(),
  reperformanceSteps: z.array(ReperformanceStepSchema),
  inlineCitations: z.array(Citation),
  auditorTrail: AuditorTrailSchema.nullable().optional(),
  reviewOrder: z.number().default(0),
  reviewerType: z.enum(["human", "system"]).default("system"),
  userId: z.string().nullable().optional(),
  columnVersion: z.number().default(0),
  runId: z.string().nullable().optional(),
  createdAt: z.string().or(z.date()).optional(),
  updatedAt: z.string().or(z.date()).optional(),
});

export type InconclusiveAttributeTestResult = z.infer<
  typeof InconclusiveAttributeTestResultSchema
>;

/**
 * Attribute Test Column Result (Discriminated Union)
 * Use isConclusive field to determine which fields are available
 * Includes attributeTestColumnId to identify which column this result belongs to
 */
export const AttributeTestColumnResultSchema = z.discriminatedUnion(
  "isConclusive",
  [ConclusiveAttributeTestResultSchema, InconclusiveAttributeTestResultSchema],
);

export type AttributeTestColumnResult = z.infer<
  typeof AttributeTestColumnResultSchema
>;

/**
 * Human-readable descriptions for control exception types
 * Maps exception enum values to display labels
 */
export const CONTROL_EXCEPTION_DESCRIPTIONS: Record<
  ControlExceptionType,
  string
> = {
  insufficient_evidence: "Insufficient Evidence",
  inconclusive_evidence: "Inconclusive Evidence",
  untimely_execution: "Untimely Execution",
  improper_authorization: "Improper Authorization",
  inconsistent_supporting_data: "Inconsistent Supporting Data",
  control_not_performed: "Control Not Performed",
  misstatement_of_amount: "Misstatement of Amount",
  cutoff_misclassification: "Cutoff Misclassification",
  general_exception: "General Exception",
};

/**
 * Human-readable descriptions for performance issue types
 */
export const PERFORMANCE_ISSUE_DESCRIPTIONS: Record<
  PerformanceIssueType,
  string
> = {
  exhausted_search: "Could not find required evidence after thorough search",
};
